/**
 * AddCurrencyDialog Component
 * 
 * Dialog for adding a new currency to the workspace.
 */

import { useEffect, type ReactElement } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Stack,
  Alert,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COMMON_CURRENCIES } from "../hooks/useDefaultCurrency";
import { getDefaultDecimalPlaces } from "../../../utils/currencyDefaults";

export interface AddCurrencyDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: () => Promise<void>;
  newCurrencyCode: { code: string; name: string; symbol: string } | null;
  setNewCurrencyCode: (value: { code: string; name: string; symbol: string } | null) => void;
  addingCurrency: boolean;
  getAvailableCurrencies: () => typeof COMMON_CURRENCIES;
  decimalPlaces: number;
  setDecimalPlaces: (value: number) => void;
}

/**
 * Dialog for adding a new currency
 */
export default function AddCurrencyDialog({
  open,
  onClose,
  onAdd,
  newCurrencyCode,
  setNewCurrencyCode,
  addingCurrency,
  getAvailableCurrencies,
  decimalPlaces,
  setDecimalPlaces,
}: AddCurrencyDialogProps): ReactElement {
  // Update decimal places when currency code changes
  useEffect(() => {
    if (newCurrencyCode) {
      const defaultDecimalPlaces = getDefaultDecimalPlaces(newCurrencyCode.code);
      setDecimalPlaces(defaultDecimalPlaces);
    }
  }, [newCurrencyCode, setDecimalPlaces]);

  const handleClose = () => {
    if (!addingCurrency) {
      setNewCurrencyCode(null);
      setDecimalPlaces(2); // Reset to default
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Add Currency</Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            disabled={addingCurrency}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
              Exchange rates are automatically fetched from the API.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              Add currencies when you need them for accounts or transactions. You can add currencies in advance for future use.
            </Typography>
          </Alert>
          <Autocomplete
            fullWidth
            options={getAvailableCurrencies()}
            value={newCurrencyCode}
            onChange={(_, newValue) => setNewCurrencyCode(newValue)}
            getOptionLabel={(option) => `${option.code} - ${option.name} (${option.symbol})`}
            filterOptions={(options, { inputValue }) => {
              const searchValue = inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  option.code.toLowerCase().includes(searchValue) ||
                  option.name.toLowerCase().includes(searchValue) ||
                  option.symbol.toLowerCase().includes(searchValue)
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Currency" required placeholder="Search currency..." />
            )}
            disabled={addingCurrency}
            ListboxProps={{
              onWheel: (e) => {
                const listbox = e.currentTarget;
                if (listbox.scrollHeight <= listbox.clientHeight) return;
                e.stopPropagation();
                e.preventDefault();
                listbox.scrollTop += e.deltaY;
              },
            }}
          />
          <TextField
            fullWidth
            select
            label="Decimal Places"
            value={decimalPlaces}
            onChange={(e) => setDecimalPlaces(Number(e.target.value))}
            disabled={addingCurrency}
            helperText={
              decimalPlaces === 0
                ? "No decimals (e.g., 1000)"
                : decimalPlaces === 2
                ? "Two decimals (e.g., 1.00)"
                : `${decimalPlaces} decimal${decimalPlaces === 1 ? "" : "s"} (e.g., ${(1).toFixed(decimalPlaces)})`
            }
          >
            <MenuItem value={0}>None (0)</MenuItem>
            <MenuItem value={1}>One (1)</MenuItem>
            <MenuItem value={2}>Two (2)</MenuItem>
            <MenuItem value={3}>Three (3)</MenuItem>
            <MenuItem value={4}>Four (4)</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={handleClose} 
          disabled={addingCurrency}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onAdd}
          disabled={!newCurrencyCode || addingCurrency}
          startIcon={addingCurrency ? <CircularProgress size={16} /> : undefined}
          sx={{ flex: 1, py: 1.5 }}
        >
          {addingCurrency ? "Adding..." : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
